import { useState, useMemo, useRef } from "react";
import {
  Users, Bell, Calendar, TrendingUp, AlertCircle, Search, X, Check, Plus,
  Filter, ChevronRight, Download, ArrowDownToLine, Loader2,
} from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card, TONE_ACCENT } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { SearchSelect } from "../../components/shared/SearchSelect";
import { PhoneTap } from "../../components/shared/PhoneTap";
import { WhatsAppTap } from "../../components/shared/WhatsAppTap";
import { DonutChart } from "../../components/shared/DonutChart";
import { toast } from "../../components/shared/Toast";
import { useAppData } from "../../lib/AppDataContext";
import {
  STATUSES, STATUS_META, SOURCES, CONFIGS, POSSESSIONS, SORT_OPTIONS,
  isAssignedTo, getAssignees, isOverdue, isToday, allowsFollowUp,
  configLabel, since, PROPERTY_TYPE_LABELS, PROPERTY_TYPE_OPTIONS,
} from "../../lib/leads";
import { KanbanBoard } from "./KanbanBoard";
import { LeadForm } from "./LeadForm";

/* LeadsList — Phase 2.

   Key differences vs Phase 1.5:
   - leadsData comes from API (already filtered server-side for employees)
   - allSalesPeople is now activeEmployees (real users)
   - Phone masking removed (employees only see their assigned = always full)
   - CSV import sends raw text to API (not client-side parse + per-row add)
   - Export button downloads from API with current filters
   - Bulk action bar wired: Assign / Change status / Mark lost */
export function LeadsList({ role, onOpen }) {
  const {
    leadsData, leadsLoading,
    activeEmployees: allSalesPeople,
    addLead, statusVersion,
    bulkLeadAction, importLeadsCsv, exportLeadsCsv,
  } = useAppData();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createdByFilter, setCreatedByFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [configFilter, setConfigFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [possessionFilter, setPossessionFilter] = useState("all");
  const [followUpDate, setFollowUpDate] = useState("");
  const [nextFollowUpDate, setNextFollowUpDate] = useState("");
  const [createdDate, setCreatedDate] = useState("");
  const [sortBy, setSortBy] = useState("createdAt-desc");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem("leads:view") || "list"; } catch { return "list"; }
  });
  const setView = (v) => { setViewState(v); try { localStorage.setItem("leads:view", v); } catch { /* ignore */ } };
  const [adding, setAdding] = useState(false);

  // Bulk action modal state
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAssignees, setBulkAssignees] = useState([]);
  const [bulkNewStatus, setBulkNewStatus] = useState("contacted");

  // Phase 2: server already filters leads for employee role.
  // No client-side `assignedTo includes me` filter needed.
  const base = leadsData;

  const sameLocalDay = (at, ymd) => {
    if (!at || !ymd) return false;
    const d = new Date(at);
    const [y, m, dd] = ymd.split("-").map(Number);
    return d.getFullYear() === y && d.getMonth() + 1 === m && d.getDate() === dd;
  };
  const onOrAfter = (at, ymd) => {
    if (!at || !ymd) return false;
    const [y, m, dd] = ymd.split("-").map(Number);
    const cutoff = new Date(y, m - 1, dd);
    return at >= cutoff;
  };

  const trimmedQuery = searchQuery.trim();
  const queryDigits = trimmedQuery.replace(/\D/g, "");
  const queryLower = trimmedQuery.toLowerCase();

  let rows = base.filter((l) => {
    if (trimmedQuery) {
      const nameMatch = l.name?.toLowerCase().includes(queryLower);
      const mobileMatch = queryDigits && (l.mobile || "").includes(queryDigits);
      if (!nameMatch && !mobileMatch) return false;
    }
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
    if (createdByFilter !== "all" && !isAssignedTo(l, createdByFilter)) return false;
    if (assignedToFilter !== "all" && !isAssignedTo(l, assignedToFilter)) return false;
    if (configFilter !== "all") {
      const cfgs = Array.isArray(l.config) ? l.config : (l.config ? [l.config] : []);
      if (!cfgs.includes(configFilter)) return false;
    }
    if (typeFilter !== "all" && l.propertyType !== typeFilter) return false;
    if (possessionFilter !== "all" && !(l.possession || []).includes(possessionFilter)) return false;
    if (followUpDate && !sameLocalDay(l.followUpAt, followUpDate)) return false;
    if (nextFollowUpDate && !onOrAfter(l.followUpAt, nextFollowUpDate)) return false;
    if (createdDate && !sameLocalDay(l.createdAt, createdDate)) return false;
    return true;
  });

  const sortDef = SORT_OPTIONS.find((o) => o.value === sortBy);
  if (sortDef && sortDef.field) {
    rows = [...rows].sort((a, b) => {
      const av = a[sortDef.field];
      const bv = b[sortDef.field];
      if (av && !bv) return sortDef.dir === 1 ? -1 : 1;
      if (!av && bv) return sortDef.dir === 1 ? 1 : -1;
      if (!av && !bv) return 0;
      return (av - bv) * sortDef.dir;
    });
  }

  // ── Pagination (client-side) ─────────────────────────────────────────
  // `base` is the full lead set from context and every filter above runs
  // client-side, so paging has to be client-side too. Mixing server paging
  // with client filtering yields pages that don't match the filters.
  //
  // safePage clamps rather than resetting: filter down to 12 rows while on
  // page 4 and you land on page 1 instead of an empty table.
  //
  // Selection is intentionally NOT cleared on page change — bulk actions
  // work on ids, so you can select across pages and act on all of them.
  const PAGE_SIZE = 20;
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(pageStart, pageStart + PAGE_SIZE);

  const configCounts = useMemo(() => {
    const counts = {};
    base.forEach((l) => {
      const cfgs = Array.isArray(l.config) ? l.config : (l.config ? [l.config] : []);
      cfgs.forEach((c) => { counts[c] = (counts[c] || 0) + 1; });
    });
    return CONFIGS.filter((c) => counts[c]).map((c) => ({ label: c, value: counts[c], cls: "text-blue-600" }));
  }, [base]);

  const statusCounts = useMemo(() => {
    const counts = {};
    base.forEach((l) => { counts[l.status] = (counts[l.status] || 0) + 1; });
    return STATUSES.filter((s) => counts[s]).map((s) => ({
      label: STATUS_META[s]?.label || s,
      value: counts[s],
      cls: ({
        new: "text-blue-500", contacted: "text-slate-500", follow_up: "text-amber-500",
        visited: "text-violet-500", re_visit: "text-purple-500", weekend: "text-cyan-500",
        token: "text-teal-500", booked: "text-green-500", hold: "text-yellow-500",
        lead_lost: "text-red-500",
      }[s]) || "text-blue-500",
    }));
  }, [base, statusVersion]);

  const activeChips = [];
  if (statusFilter !== "all") activeChips.push({ key: "status", label: `Status: ${STATUS_META[statusFilter]?.label || statusFilter}`, clear: () => setStatusFilter("all") });
  if (sourceFilter !== "all") activeChips.push({ key: "source", label: `Source: ${sourceFilter}`, clear: () => setSourceFilter("all") });
  if (createdByFilter !== "all") activeChips.push({ key: "createdBy", label: `Created by: ${allSalesPeople.find((s) => (s.userId || s.id) === createdByFilter)?.name || createdByFilter}`, clear: () => setCreatedByFilter("all") });
  if (assignedToFilter !== "all") activeChips.push({ key: "assignedTo", label: `Assigned to: ${allSalesPeople.find((s) => (s.userId || s.id) === assignedToFilter)?.name || assignedToFilter}`, clear: () => setAssignedToFilter("all") });
  if (configFilter !== "all") activeChips.push({ key: "config", label: `Config: ${configFilter}`, clear: () => setConfigFilter("all") });
  if (possessionFilter !== "all") activeChips.push({ key: "possession", label: `Possession: ${possessionFilter}`, clear: () => setPossessionFilter("all") });
  if (followUpDate) activeChips.push({ key: "followUpDate", label: `Follow-up on: ${followUpDate}`, clear: () => setFollowUpDate("") });
  if (nextFollowUpDate) activeChips.push({ key: "nextFollowUpDate", label: `Next follow-up ≥ ${nextFollowUpDate}`, clear: () => setNextFollowUpDate("") });
  if (createdDate) activeChips.push({ key: "createdDate", label: `Created on: ${createdDate}`, clear: () => setCreatedDate("") });

  const clearAll = () => {
    setStatusFilter("all"); setSourceFilter("all"); setCreatedByFilter("all");
    setAssignedToFilter("all"); setConfigFilter("all"); setPossessionFilter("all");
    setFollowUpDate(""); setNextFollowUpDate(""); setCreatedDate(""); setSortBy("createdAt-desc");
  };

  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const todaysLeads = base.filter((l) => isToday(l.createdAt));
  const todaysFollowUps = base.filter((l) => isToday(l.followUpAt) && allowsFollowUp(l.status));
  const unassigned = base.filter((l) => getAssignees(l).length === 0);
  const converted = base.filter((l) => l.status === "booked");
  // `tone` tints each tile by what its number MEANS. Total Leads stays
  // neutral on purpose — it is context, and keeping it white is what lets
  // the tinted tiles beside it read as signal. The rest are conditional so
  // a quiet day looks quiet and a backlog looks urgent.
  const leadsKPIs = [
    { label: "Total Leads",       value: base.length,            sub: role === "employee" ? "assigned to you" : "in the pipeline", icon: Users, tone: "indigo" },
    { label: "Today's Leads",     value: todaysLeads.length,     sub: "captured today",                                          icon: Bell, tone: "info" },
    { label: "Today's Follow-up", value: todaysFollowUps.length, sub: "scheduled for today",                                     icon: Calendar, tone: "warning" },
    role === "employee"
      ? { label: "Converted Leads",  value: converted.length,    sub: converted.length > 0 ? "bookings closed" : "none yet",     icon: TrendingUp, tone: "success" }
      : { label: "Unassigned Leads", value: unassigned.length,   sub: unassigned.length > 0 ? "needs routing" : "all assigned",  icon: AlertCircle, tone: "danger" },
  ];

  const newLeadsCard = base
    .filter((l) => l.status === "new")
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 6);
  const scheduleCard = base
    .filter((l) => isToday(l.followUpAt) && allowsFollowUp(l.status))
    .sort((a, b) => a.followUpAt - b.followUpAt);
  const teamByIdLeads = Object.fromEntries(allSalesPeople.map((s) => [s.userId || s.id, s]));  // Phase 5: key by user.id (leads.assigned_to stores user ids)
  const formatLeadTime = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

  // ── CSV import: send raw text to API, show summary toast ─────────────
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const text = String(ev.target?.result || "");
        const res = await importLeadsCsv(text);
        const skipMsg = res.skipped?.length ? ` · ${res.skipped.length} skipped` : "";
        toast(`Imported ${res.imported} lead${res.imported === 1 ? "" : "s"}${skipMsg}`);
        if (res.skipped?.length) {
          console.warn("CSV import skipped rows:", res.skipped);
        }
      } catch (err) {
        console.error(err);
        toast(err?.message || "CSV import failed");
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  // ── Export: current filters → CSV download ───────────────────────────
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    try {
      setExporting(true);
      const filters = {};
      if (statusFilter !== "all") filters.status = statusFilter;
      if (sourceFilter !== "all") filters.source = sourceFilter;
      if (assignedToFilter !== "all") filters.assignedUserId = assignedToFilter;
      if (configFilter !== "all") filters.config = configFilter;
      if (possessionFilter !== "all") filters.possession = possessionFilter;
      if (trimmedQuery) filters.search = trimmedQuery;
      await exportLeadsCsv(filters);
    } catch (err) {
      toast(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ── Bulk: Assign ─────────────────────────────────────────────────────
  const runBulkAssign = async () => {
    if (bulkAssignees.length === 0 && !confirm("Unassign all selected leads?")) return;
    try {
      setBulkBusy(true);
      const res = await bulkLeadAction({
        leadIds: Array.from(selected),
        action: "assign",
        assignedTo: bulkAssignees,
      });
      toast(`Updated ${res.affected} lead${res.affected === 1 ? "" : "s"}`);
      setSelected(new Set());
      setBulkAssignOpen(false);
      setBulkAssignees([]);
    } catch (err) {
      toast(err?.message || "Bulk assign failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // ── Bulk: Change status ──────────────────────────────────────────────
  const runBulkStatus = async () => {
    try {
      setBulkBusy(true);
      const res = await bulkLeadAction({
        leadIds: Array.from(selected),
        action: "status",
        status: bulkNewStatus,
      });
      toast(`Updated ${res.affected} lead${res.affected === 1 ? "" : "s"}`);
      setSelected(new Set());
      setBulkStatusOpen(false);
    } catch (err) {
      toast(err?.message || "Bulk status change failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // ── Bulk: Mark lost (one-click, no modal) ────────────────────────────
  const runBulkMarkLost = async () => {
    if (!confirm(`Mark ${selected.size} lead${selected.size === 1 ? "" : "s"} as lost?`)) return;
    try {
      setBulkBusy(true);
      const res = await bulkLeadAction({
        leadIds: Array.from(selected),
        action: "status",
        status: "lead_lost",
      });
      toast(`Marked ${res.affected} as lost`);
      setSelected(new Set());
    } catch (err) {
      toast(err?.message || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <PageHeader accent="sales"
        title="Real Estate Leads"
        subtitle={role === "employee" ? "Leads assigned to you" : `${rows.length} of ${base.length} leads`}
        actions={<>
          <div className="inline-flex rounded-lg ring-1 ring-inset ring-slate-300 bg-white p-0.5">
            <button onClick={() => setView("list")} title="List view"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === "list" ? "bg-[#162a52] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              List
            </button>
            <button onClick={() => setView("grid")} title="Grid view"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === "grid" ? "bg-[#162a52] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Grid
            </button>
          </div>
          {role !== "employee" && (
            <Btn variant="secondary" size="md" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 rotate-180" />}
              Import
            </Btn>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImport}
            className="hidden"
          />
          {role !== "employee" && (
            <Btn variant="secondary" size="md" onClick={handleExport} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
            </Btn>
          )}
          <Btn variant="primary" size="md" onClick={() => setAdding(true)}><Plus className="w-4 h-4" />Add Lead</Btn>
        </>}
      />

      {leadsLoading && base.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400">
          <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Loading leads…
        </div>
      ) : null}

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {leadsKPIs.map((k) => (
          <Card key={k.label} tone={k.tone} className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-600">{k.label}</div>
                <div className={`text-2xl font-bold mt-1 tabular-nums ${(TONE_ACCENT[k.tone] || TONE_ACCENT.default).text}`}>{k.value}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{k.sub}</div>
              </div>
              <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${(TONE_ACCENT[k.tone] || TONE_ACCENT.default).chip}`}>
                <k.icon className="w-4 h-4 text-white" />
              </span>
            </div>
          </Card>
        ))}
      </div>

      {/* 4-up row: New Leads + Today's Schedule + 2 DonutCharts. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Card tone="info" className="p-4">
          <div className="-mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-2xl border-b bg-blue-100 border-blue-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 rounded-md grid place-items-center shrink-0 bg-blue-500">
                <Bell className="w-3.5 h-3.5 text-white" />
              </span>
              <h3 className="text-sm font-semibold text-slate-900">New leads</h3>
            </div>
            <span className="text-[10px] font-medium text-slate-600 shrink-0">{newLeadsCard.length} new</span>
          </div>
          {newLeadsCard.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">No new leads &mdash; you're all caught up.</div>
          ) : (
            <div className="space-y-1 max-h-[260px] overflow-y-auto overflow-x-hidden -mx-1 px-1">
              {newLeadsCard.map((l) => (
                <div key={l.id} onClick={() => onOpen(l)} className="flex items-center gap-2 py-1.5 px-1.5 -mx-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center text-[10px] font-semibold text-slate-600 shrink-0">
                    {l.name.split(" ").map((p) => p[0]).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-900 truncate">{l.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{l.project} &middot; {l.source === "Other" && l.sourceOther ? l.sourceOther : l.source}</div>
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0">{since(l.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card tone="warning" className="p-4">
          <div className="-mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-2xl border-b bg-amber-100 border-amber-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 rounded-md grid place-items-center shrink-0 bg-amber-500">
                <Calendar className="w-3.5 h-3.5 text-white" />
              </span>
              <h3 className="text-sm font-semibold text-slate-900">Today's schedule</h3>
            </div>
            <span className="text-[10px] font-medium text-slate-600 shrink-0">{scheduleCard.length} follow-ups</span>
          </div>
          {scheduleCard.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">No follow-ups scheduled for today.</div>
          ) : (
            <div className="space-y-1 max-h-[260px] overflow-y-auto overflow-x-hidden -mx-1 px-1">
              {scheduleCard.map((l) => {
                const assigneeIds = getAssignees(l);
                const firstOwner = assigneeIds.length > 0 ? teamByIdLeads[assigneeIds[0]] : null;
                const extraCount = Math.max(0, assigneeIds.length - 1);
                return (
                  <div key={l.id} onClick={() => onOpen(l)} className="flex items-center gap-2 py-1.5 px-1.5 -mx-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <div className="w-12 text-[10px] font-semibold text-blue-700 tabular-nums shrink-0">{formatLeadTime(l.followUpAt)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-900 truncate">{l.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{l.project}{firstOwner ? ` · ${firstOwner.name.split(" ")[0]}${extraCount > 0 ? ` +${extraCount}` : ""}` : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <DonutChart title="Leads by config"  data={configCounts} accent="violet" />
        <DonutChart title="Leads by status"  data={statusCounts} accent="teal" />
      </div>

      {/* Toolbar — search + sort + filter button + selection actions. */}
      <Card>
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or phone"
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 pl-8 pr-8 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-blue-500" title="Sort">
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value === "default" ? "Sort: Default" : o.label}</option>)}
          </select>

          <button
            onClick={() => setFiltersOpen(true)}
            className={`text-xs font-medium px-3 py-1.5 rounded-lg ring-1 ring-inset transition-colors inline-flex items-center gap-1.5 ${activeChips.length > 0 ? "bg-blue-50 ring-blue-300 text-blue-800" : "bg-white ring-slate-300 text-slate-700 hover:bg-slate-50"}`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filters
            {activeChips.length > 0 && (
              <span className="bg-blue-600 text-white rounded-full min-w-[18px] h-[18px] grid place-items-center text-[10px] font-bold px-1">{activeChips.length}</span>
            )}
          </button>

          {/* Employees get Transfer only, so they can hand several of their
              own leads to someone else at once. Status in bulk makes no sense
              for them -- each lead sits at its own stage -- and Mark lost in
              bulk could empty a pipeline with one click. The API enforces the
              same limit, and restricts an employee's bulk update to leads
              already assigned to them. */}
          {selected.size > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">{selected.size} selected</span>
              <Btn variant="secondary" size="sm" onClick={() => setBulkAssignOpen(true)} disabled={bulkBusy}>
                {role === "employee" ? "Transfer" : "Assign"}
              </Btn>
              {role !== "employee" && (
                <>
                  <Btn variant="secondary" size="sm" onClick={() => setBulkStatusOpen(true)} disabled={bulkBusy}>Change status</Btn>
                  <Btn variant="ghost" size="sm" className="text-red-600" onClick={runBulkMarkLost} disabled={bulkBusy}>Mark lost</Btn>
                </>
              )}
            </div>
          )}
          <span className="ml-auto text-xs text-slate-400 inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />Overdue follow-up
          </span>
        </div>

        {activeChips.length > 0 && (
          <div className="px-3 py-2 bg-blue-50/40 border-b border-slate-200 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-slate-500">Active:</span>
            {activeChips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1 text-[11px] bg-white ring-1 ring-inset ring-slate-300 rounded-full px-2 py-0.5">
                {c.label}
                <button onClick={c.clear} className="text-slate-400 hover:text-red-600" title="Remove filter">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button onClick={clearAll} className="ml-2 text-[11px] font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2">Clear all</button>
          </div>
        )}

        {view === "list" && (
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead>
                <tr className="sticky top-0 z-20 text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2.5 w-8"></th>
                  <th className="px-3 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Phone</th>
                  <th className="px-3 py-2.5">WhatsApp</th>
                  <th className="px-3 py-2.5">Type</th>
                  <th className="px-3 py-2.5">Config</th>
                  <th className="px-3 py-2.5">Source</th>
                  {role !== "employee" && <th className="px-3 py-2.5">Assigned</th>}
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Follow-up</th>
                  <th className="px-3 py-2.5">Remark</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((l) => {
                  // Phase 2: all visible leads show full phone. Employees only
                  // see their assigned (server-filtered), so no masking needed.
                  const overdue = isOverdue(l);
                  const rowCls = overdue
                    ? "bg-red-50 hover:bg-red-100 cursor-pointer ring-1 ring-inset ring-red-200"
                    : "hover:bg-slate-50 cursor-pointer";
                  const waNumber = l.whatsapp || l.mobile;
                  return (
                    <tr key={l.id} className={rowCls} onClick={() => onOpen(l)}>
                      {/* Employees need this to select leads for bulk transfer.
                          Must match the header cell: a header column with no
                          body cells shifts every row one column left. */}
                      <td className="px-3 py-2.5" onClick={(e) => { e.stopPropagation(); toggle(l.id); }}>
                        <div className={`w-4 h-4 rounded border ${selected.has(l.id) ? "bg-blue-600 border-blue-600" : "border-slate-300"} grid place-items-center`}>
                          {selected.has(l.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-slate-900">{l.name}</td>
                      <td className="px-3 py-2.5">
                        <PhoneTap mobile={l.mobile} whatsapp={l.whatsapp} display={l.phone} />
                        {l.whatsappOnly && (
                          <span className="ml-1.5 text-[9px] font-semibold rounded-pill bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 px-1.5 py-0.5">
                            WA
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <WhatsAppTap mobile={waNumber} label={"+91 " + waNumber.slice(0, 5) + " " + waNumber.slice(5)} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{l.propertyType ? PROPERTY_TYPE_LABELS[l.propertyType] || l.propertyType : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-slate-600">{configLabel(l.config) || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2.5"><Badge cls="bg-slate-100 text-slate-600 ring-slate-400/20">{l.source}</Badge></td>
                      {role !== "employee" && (
                        <td className="px-3 py-2.5 text-slate-600">
                          {(() => {
                            const ids = getAssignees(l);
                            if (ids.length === 0) return <span className="text-amber-600 text-xs font-medium">Unassigned</span>;
                            const names = ids
                              .map((id) => allSalesPeople.find((s) => (s.userId || s.id) === id)?.name || "Employee")
                              .filter(Boolean);
                            if (names.length === 1) return names[0];
                            return (
                              <span title={names.join(", ")}>
                                {names[0]} <span className="text-slate-400 font-medium">+{names.length - 1}</span>
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-3 py-2.5"><StatusBadge status={l.status} /></td>
                      <td className="px-3 py-2.5 text-xs">
                        {l.followUpAt ? (
                          <span className={overdue ? "text-red-700 font-semibold" : "text-slate-600"}>
                            {overdue && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 align-middle" />}
                            {l.followUpAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · {l.followUpAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">
                        {l.remark ? (
                          <span
                            title={l.remark}
                            className="inline-block max-w-[220px] truncate align-middle"
                          >
                            {l.remark}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                    </tr>
                  );
                })}
                {totalRows === 0 && !leadsLoading && (
                  <tr><td colSpan={11} className="px-3 py-12 text-center text-sm text-slate-400">
                    No leads match your filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pager — list view only. The Kanban board is deliberately not
            paged: a board split across pages stops being a board. */}
        {view === "list" && totalRows > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 border-t border-slate-200">
            <span className="text-xs text-slate-500 tabular-nums">
              {pageStart + 1}&ndash;{Math.min(pageStart + PAGE_SIZE, totalRows)} of {totalRows}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage <= 1}
                  aria-label="Previous page"
                  className="w-8 h-8 rounded-lg grid place-items-center text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>

                {/* Windowed page numbers: first, last, and a range around the
                    current page. Keeps the pager fixed-width as lead volume
                    grows instead of rendering forty buttons. */}
                {(() => {
                  const win = [];
                  const push = (n) => { if (!win.includes(n)) win.push(n); };
                  push(1);
                  for (let n = safePage - 1; n <= safePage + 1; n++) {
                    if (n > 1 && n < totalPages) push(n);
                  }
                  if (totalPages > 1) push(totalPages);
                  win.sort((a, b) => a - b);

                  const out = [];
                  win.forEach((n, i) => {
                    const prev = win[i - 1];
                    if (prev && n - prev > 1) {
                      out.push(
                        <span key={`gap-${n}`} className="w-6 text-center text-xs text-slate-400 select-none">&hellip;</span>
                      );
                    }
                    out.push(
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPage(n)}
                        aria-current={n === safePage ? "page" : undefined}
                        className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold tabular-nums transition-colors ${
                          n === safePage
                            ? "bg-[#162a52] text-white"
                            : "text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        {n}
                      </button>
                    );
                  });
                  return out;
                })()}

                <button
                  type="button"
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage >= totalPages}
                  aria-label="Next page"
                  className="w-8 h-8 rounded-lg grid place-items-center text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {view === "grid" && (
          <KanbanBoard rows={rows} statusFilter={statusFilter} role={role} onOpen={onOpen} />
        )}
      </Card>

      {adding && (
        <LeadForm
          lead={null}
          role={role}
          statusVersion={statusVersion}
          onClose={() => setAdding(false)}
          onSave={async (newLead) => {
            try {
              await addLead(newLead);
              setAdding(false);
              toast("Lead added");
            } catch (err) {
              toast(err?.message || "Failed to add lead");
              throw err;
            }
          }}
          /* ensure list reflects new lead immediately */
        />
      )}

      {/* Bulk assign modal */}
      {bulkAssignOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setBulkAssignOpen(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <Card className="overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="text-base font-semibold text-slate-900">Assign {selected.size} lead{selected.size === 1 ? "" : "s"}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Pick one or more employees. Empty = unassign.</p>
              </div>
              <div className="p-5 space-y-2 max-h-[50vh] overflow-y-auto">
                {allSalesPeople.length === 0 ? (
                  <p className="text-xs text-amber-600 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
                    No active employees. Invite from Settings → Users first.
                  </p>
                ) : allSalesPeople.filter((s) => s.active !== false && s.userActive !== false && s.role === "employee").map((s) => {
                  // userId, not id: leads.assigned_to stores USER ids, and the
                  // API validates them against the user table. An employee has
                  // both an employee record and a user account with different
                  // ids, so sending s.id failed for every employee with "One or
                  // more assignee IDs are invalid". LeadForm already does this.
                  const assigneeId = s.userId || s.id;
                  const checked = bulkAssignees.includes(assigneeId);
                  return (
                    <label key={s.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ring-1 ring-inset cursor-pointer ${checked ? "bg-blue-50 ring-blue-300 text-blue-900" : "ring-slate-300 hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={checked}
                        onChange={(e) => setBulkAssignees(e.target.checked
                          ? [...bulkAssignees, assigneeId]
                          : bulkAssignees.filter((x) => x !== assigneeId))}
                        className="w-4 h-4 rounded text-blue-600" />
                      <span className="text-sm font-medium">{s.name}</span>
                    </label>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
                <Btn variant="ghost" onClick={() => setBulkAssignOpen(false)} disabled={bulkBusy}>Cancel</Btn>
                <Btn variant="primary" onClick={runBulkAssign} disabled={bulkBusy}>
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Apply
                </Btn>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Bulk status modal */}
      {bulkStatusOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setBulkStatusOpen(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <Card className="overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="text-base font-semibold text-slate-900">Change status for {selected.size} lead{selected.size === 1 ? "" : "s"}</h3>
              </div>
              <div className="p-5">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">New status</label>
                <select value={bulkNewStatus} onChange={(e) => setBulkNewStatus(e.target.value)}
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                </select>
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
                <Btn variant="ghost" onClick={() => setBulkStatusOpen(false)} disabled={bulkBusy}>Cancel</Btn>
                <Btn variant="primary" onClick={runBulkStatus} disabled={bulkBusy}>
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Apply
                </Btn>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Filter modal */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={() => setFiltersOpen(false)}>
          <div className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <Card className="overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Filter leads</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{activeChips.length} active &middot; changes apply live</p>
                </div>
                <button onClick={() => setFiltersOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                    <option value="all">All statuses</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Source</label>
                  <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                    <option value="all">All sources</option>
                    {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Created by</label>
                  {/* Searchable: a plain select means scrolling once the team
                      grows past a screenful. */}
                  <SearchSelect value={createdByFilter} onChange={setCreatedByFilter}
                    options={[{ id: "all", name: "Anyone" }, ...allSalesPeople.map((s) => ({ id: s.userId || s.id, name: s.name }))]} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Assigned to</label>
                  {/* Searchable, as with Created by above. */}
                  <SearchSelect value={assignedToFilter} onChange={setAssignedToFilter}
                    options={[{ id: "all", name: "Anyone" }, ...allSalesPeople.map((p) => ({ id: p.userId || p.id, name: p.name }))]} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Property type</label>
                  <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                    <option value="all">Any type</option>
                    {PROPERTY_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Config</label>
                  <select value={configFilter} onChange={(e) => setConfigFilter(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                    <option value="all">Any config</option>
                    {CONFIGS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Possession</label>
                  <select value={possessionFilter} onChange={(e) => setPossessionFilter(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
                    <option value="all">Any possession</option>
                    {POSSESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Follow-up on (exact date)</label>
                  <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Next follow-up on/after</label>
                  <input type="date" value={nextFollowUpDate} onChange={(e) => setNextFollowUpDate(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Created on</label>
                  <input type="date" value={createdDate} onChange={(e) => setCreatedDate(e.target.value)} className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500" />
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex justify-between gap-2 bg-slate-50">
                <Btn variant="ghost" onClick={clearAll}>Clear all</Btn>
                <Btn variant="primary" onClick={() => setFiltersOpen(false)}><Check className="w-4 h-4" />Done</Btn>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
